/**
 * Admin-Web-Terminal (nur volle Reviewer): PTY-Shell des Instanz-Benutzers.
 *
 * Hauptzweck: der einmalige `claude`-OAuth-Login des Instanz-Abos — danach
 * kann der KI-Provider „claude-abo" (Admin → KI) die Anmeldung nutzen und
 * alle KI-Dienste (Erfassungs-Chat, Strukturierung, …) laufen über das Abo
 * statt über API-Guthaben.
 */
import { useEffect, useRef } from "react"
import { Terminal as XTerm } from "@xterm/xterm"
import { FitAddon } from "@xterm/addon-fit"
import { WebglAddon } from "@xterm/addon-webgl"
import "@xterm/xterm/css/xterm.css"

import { api, apiError } from "@/services/api"
import { useAuthStore } from "@/store/auth"

function wsUrl(ticket: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:"
  return `${proto}//${window.location.host}/tester/api/v1/admin/terminal/ws?ticket=${encodeURIComponent(ticket)}`
}

export function AdminTerminalPage() {
  const hostRef = useRef<HTMLDivElement>(null)
  // Aufruf IM Selector: liefert einen Boolean, der sich beim Eintreffen der
  // Rollen ändert. Die blanke Funktionsreferenz (s.isReviewerVoll) ist stabil
  // und löste nach dem Auth-Laden kein Re-Render aus — Seite blieb nach
  // Browser-Refresh fälschlich auf der Sperr-Meldung stehen.
  const istVoll = useAuthStore((s) => s.isReviewerVoll())
  const ladend = useAuthStore((s) => s.isLoading)

  useEffect(() => {
    if (!hostRef.current || !istVoll) return

    const term = new XTerm({
      fontSize: 13,
      fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
      cursorBlink: true,
      theme: { background: "#0b0f1a", foreground: "#e8edf7", cursor: "#5b8def" },
    })
    const fit = new FitAddon()
    term.loadAddon(fit)
    term.open(hostRef.current)
    // WebGL-Renderer: zeichnet Box-/Braille-Zeichen (TUI-Rahmen, Spinner)
    // selbst statt über Fallback-Fonts des Browsers — der DOM-Renderer
    // ließ sie überlappen. Ohne WebGL (Software-Rendering) bleibt der
    // DOM-Renderer als Fallback.
    try {
      const webgl = new WebglAddon()
      webgl.onContextLoss(() => webgl.dispose())
      term.loadAddon(webgl)
    } catch {
      // WebGL nicht verfügbar — funktional identisch, nur Glyphen-Optik
    }
    fit.fit()

    let ws: WebSocket | null = null
    let geschlossen = false
    let resizeTimer: number | undefined
    let letzteCols = 0
    let letzteRows = 0

    const sendResize = () => {
      fit.fit()
      // Nur echte Größenänderungen ans PTY melden — jedes resize löst dort
      // SIGWINCH + Prompt-Neuzeichnung aus.
      if (term.cols === letzteCols && term.rows === letzteRows) return
      letzteCols = term.cols
      letzteRows = term.rows
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }))
      }
    }

    // Entprellt; fit() nur, wenn sich Spalten/Zeilen wirklich ändern würden —
    // sonst Endlosschleife fit↔resize (Zittern/unendliches Wachstum).
    const ro = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(() => {
        const dims = fit.proposeDimensions()
        if (!dims || (dims.cols === term.cols && dims.rows === term.rows)) return
        sendResize()
      }, 150)
    })
    ro.observe(hostRef.current)

    const verbinden = async () => {
      // Zweite Auth-Stufe (#58): kein Access-Token in der WS-URL. Erst ein
      // kurzlebiges Einmal-Ticket über die normale API-Auth holen, damit
      // den WebSocket öffnen.
      let ticket: string
      try {
        ticket = (await api.admin.terminal.ticket()).ticket
      } catch (e) {
        term.writeln(`\x1b[31mTerminal-Ticket nicht erhalten: ${apiError(e)}\x1b[0m`)
        return
      }
      if (geschlossen) return
      ws = new WebSocket(wsUrl(ticket))

      ws.onopen = () => {
        term.writeln(
          "\x1b[2mVerbunden. Abo-Login der Instanz:  claude  eingeben (OAuth im Browser).\x1b[0m"
        )
        sendResize()
      }
      ws.onmessage = (e) => {
        const m = JSON.parse(e.data)
        if (m.type === "output") term.write(m.data)
        else if (m.type === "exit") term.writeln("\r\n\x1b[2m[Sitzung beendet]\x1b[0m")
      }
      ws.onclose = () => term.writeln("\r\n\x1b[2m[Verbindung getrennt]\x1b[0m")

      term.onData((d) => {
        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "input", data: d }))
      })
    }
    void verbinden()

    return () => {
      geschlossen = true
      window.clearTimeout(resizeTimer)
      ro.disconnect()
      ws?.close()
      term.dispose()
    }
  }, [istVoll])

  if (!istVoll) {
    return (
      <div className="p-8 text-muted-foreground">
        {ladend
          ? "Anmeldung wird geprüft …"
          : "Das Admin-Terminal ist netz-fabrik-Reviewern vorbehalten."}
      </div>
    )
  }

  return (
    // Feste Höhe statt h-full: <main> im MainLayout hat keine Höhenbegrenzung
    // (min-h-screen außen) — h-full läuft dort ins Leere und der Container
    // bekäme seine Höhe vom Inhalt → fit() vergrößert das Terminal → Container
    // wächst → Observer → fit() … (unendlich wachsendes Terminal). 8rem =
    // h-14 Kopfleiste + py-9 des <main> (etabliertes Muster, s. ErfassungPage).
    <div className="flex flex-col gap-3 p-4" style={{ height: "calc(100vh - 8rem)" }}>
      <div>
        <h1 className="text-xl font-semibold">Terminal</h1>
        <p className="text-sm text-muted-foreground">
          Shell des Instanz-Benutzers — für den <code>claude</code>-Abo-Login (OAuth) und
          Instanz-Wartung. Danach steht der KI-Anbieter „claude-abo" zur Verfügung.
        </p>
      </div>
      {/* Padding am Wrapper, NICHT am Mess-Element: FitAddon liest die
          border-box-Höhe — p-2 direkt am Host ergab ~16px zu viele Zeilen,
          die unterste (Claude-Eingabezeile) wurde abgeschnitten. */}
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border bg-[#0b0f1a] p-2">
        <div ref={hostRef} className="h-full w-full" />
      </div>
    </div>
  )
}
